/**
 * TASK-APS-005: Dust Particle Update Logic
 *
 * Updates dust particles with upward movement, horizontal sway, top-edge fade, and respawn.
 * Dust particles float upward with oscillating horizontal motion, fade as they reach the top,
 * and respawn at the bottom.
 */

import type { Particle } from './particle-pool';
import type { DustConfig } from '../../types/terrain';

/**
 * Updates a dust particle's position with sway, fade, and respawn
 *
 * Dust particles:
 * - Move upward (negative Y velocity)
 * - Sway horizontally using sine wave oscillation
 * - Fade out as they approach the top edge (20% fade zone)
 * - Respawn at bottom when they pass the top edge
 *
 * @param particle - Particle to update (mutated in place)
 * @param config - Dust configuration
 * @param deltaTime - Time elapsed since last update (seconds)
 * @param time - Current time for sway calculation (seconds)
 */
export function updateDustParticle(
  particle: Particle,
  config: DustConfig,
  deltaTime: number,
  time: number
): void {
  // Move particle upward by velocity
  particle.y += particle.vy * deltaTime;

  // Apply horizontal sway using sine wave
  // Each particle gets a unique phase offset based on their baseAlpha (acts as seed)
  const phaseOffset = particle.baseAlpha * Math.PI * 2;
  const swayOffset =
    Math.sin(time * config.swayFrequency + phaseOffset) * config.swayAmplitude * deltaTime;
  particle.x += swayOffset;

  // Calculate fade at top edge (fade zone = 20% of height)
  const topEdge = config.bounds.y;
  const fadeZone = config.bounds.height * 0.2;

  if (particle.y < topEdge + fadeZone) {
    // Particle is in fade zone
    const distanceFromTop = particle.y - topEdge;
    const fadeProgress = Math.max(0, Math.min(1, distanceFromTop / fadeZone));
    particle.alpha = particle.baseAlpha * fadeProgress;
  } else {
    // Particle is below fade zone, use full alpha
    particle.alpha = particle.baseAlpha;
  }

  // Respawn at bottom when particle passes top edge
  if (particle.y < topEdge) {
    respawnDustParticle(particle, config);
  }
}

/**
 * Respawns a dust particle at the bottom with reset properties
 *
 * @param particle - Particle to respawn
 * @param config - Dust configuration
 */
function respawnDustParticle(particle: Particle, config: DustConfig): void {
  // Random X position within bounds
  particle.x = config.bounds.x + Math.random() * config.bounds.width;

  // Position at bottom edge
  particle.y = config.bounds.y + config.bounds.height;

  // Reset alpha to base value
  particle.alpha = particle.baseAlpha;

  // Assign new upward velocity within configured range
  const speed = config.speed.min + Math.random() * (config.speed.max - config.speed.min);
  particle.vy = -speed; // Negative for upward movement
}
